import type {
  BatchLiabilityResultItem,
  SpecificEmbeddedEmissionsSource
} from '@/features/cbam-reporting/hooks/cbamLiability'
import type { Quarter } from '@/config/cbamReportingPeriods'
import type { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import { computeCbamLiabilityEuro } from '@/shared/utils/computeCbamLiabilityEuro'
import type { TFunction } from 'i18next'

type LiabilityOverviewGoodFacts = {
  quarter: Quarter | null | undefined
  cnCode: string
  description?: string | null
  netMassInTonnes: number
  isUsingDefaultEmissions: boolean
  /** ISO country of origin (for default-values source label). */
  country: string
}

/** Sectors where CBAM rules often exclude indirect emissions when reported as zero (template section 5). */
const SECTORS_INDIRECT_EXCLUSION_RULE = new Set<string>([
  'UNWROUGHT_ALUMINIUM',
  'ALUMINIUM_PRODUCTS',
  'HYDROGEN',
  'SINTERED_ORE',
  'PIG_IRON',
  'FE_MN',
  'FE_CR',
  'FE_NI',
  'DIRECT_REDUCED_IRON',
  'CRUDE_STEEL',
  'IRON_OR_STEEL_PRODUCTS'
])

export function sectorUsesIndirectEmissionExclusionRule(aggregatedGoodsCategory: string): boolean {
  return SECTORS_INDIRECT_EXCLUSION_RULE.has(aggregatedGoodsCategory)
}

/** Matches CN code labels elsewhere (e.g. `CnCodeTag`, liability tables). */
export function resolveLiabilityOverviewGoodsName(
  cnCode: string,
  description: string | null | undefined,
  t: TFunction
): string {
  const trimmed = description?.trim()
  if (trimmed) {
    return trimmed
  }
  const fromCatalog = String(t(cnCode, { ns: 'goods', defaultValue: '' })).trim()
  if (fromCatalog) {
    return fromCatalog
  }
  return '-'
}

type BatchSelectionReason = {
  usedOtcFallback?: boolean
}

export type LiabilityOverviewViewModel = {
  cnCode: string
  goodsName: string
  year: number
  volumeTonnes: number
  quarter: Quarter | null | undefined
  /** Aggregated goods category key for i18n; `null` when unknown (avoid translating sentinels). */
  sector: string | null
  specificDirectTco2PerT: number
  specificIndirectTco2PerT: number
  cbamBenchmarkTco2PerT: number
  intermediateBenchmarkPerT: number
  cbamFactor: number
  cscfPercentage: number
  discountPerTonneTco2: number
  certificatePriceEurPerT: number
  priceSource: 'official' | 'estimated'
  specificEmbeddedEmissionsSource: SpecificEmbeddedEmissionsSource
  defaultMarkupPercent: number | null
  totalDirectTco2: number
  totalIndirectTco2: number
  totalEmbeddedTco2: number
  totalDiscountTco2: number
  netEmissionsTco2: number
  liabilityEur: number
  isUsingDefaultEmissions: boolean
  actualValueSource: string | null
  defaultValueSource: 'origin-country' | 'other-countries-and-territories'
  routeLetter: string | null
  indirectEffectivelyZero: boolean
  sectorSubjectToIndirectExclusionRule: boolean
  benchmarkDisclaimer: string | null
}

export type BuildLiabilityOverviewModelArgs = {
  liabilityData: BatchLiabilityResultItem
  good: LiabilityOverviewGoodFacts
  supplierEmissionsReport: SupplierEmissionsReport | null
  modifiers: CbamModifiers
  projectionYear: number
  aggregatedGoodsCategory?: string
  t: TFunction
}

export function buildLiabilityOverviewModel({
  liabilityData,
  good,
  supplierEmissionsReport,
  modifiers,
  projectionYear,
  aggregatedGoodsCategory,
  t
}: BuildLiabilityOverviewModelArgs): LiabilityOverviewViewModel | null {
  const result = liabilityData.result
  const projection = result?.projection?.[String(projectionYear)]
  if (!result || !projection) {
    return null
  }

  const quarter = good.quarter
  const quarterModifier = quarter ? modifiers.euaPriceQuarters[quarter] : undefined
  const certificatePriceEurPerT = Number.parseFloat(quarterModifier?.value ?? '') || 0
  /** Base batch liability uses CSCF 100%; overview must match projection numbers (not modifiers). */
  const cscfPercentage = 100

  const selectionReason = result.parameters?.selectionReason as BatchSelectionReason
  const defaultValueSource = selectionReason?.usedOtcFallback
    ? 'other-countries-and-territories'
    : 'origin-country'

  const rawRouteLetter = result.parameters?.benchmark?.validation?.defaultProductionRoute
  const routeLetter = rawRouteLetter?.trim().toUpperCase() || null

  const benchmarkT = projection.benchmarkValue ?? 0
  const intermediateBenchmarkPerT = benchmarkT * projection.cbamFactor
  const discountPerTonneTco2 = projection.specificFreeAllocationTCo2Pt ?? 0
  const specificEmbedded = result.parameters.specificEmbeddedEmissions
  const detailsAvailable = specificEmbedded.details?.available
  const defaultMarkupValueByYear =
    projectionYear >= 2028
      ? detailsAvailable?.defaultValue2028OnwardsIncludingMarkUp
      : projectionYear === 2027
        ? detailsAvailable?.defaultValue2027IncludingMarkUp
        : detailsAvailable?.defaultValue2026IncludingMarkUp
  let defaultMarkupPercent: number | null = null
  if (
    detailsAvailable?.defaultValueDirectEmissions != null &&
    defaultMarkupValueByYear != null &&
    detailsAvailable.defaultValueDirectEmissions > 0
  ) {
    defaultMarkupPercent =
      ((defaultMarkupValueByYear - detailsAvailable.defaultValueDirectEmissions) /
        detailsAvailable.defaultValueDirectEmissions) *
      100
  }

  const specificIndirect = projection.specificIndirectEmbeddedEmissions ?? 0
  const indirectEffectivelyZero =
    Math.abs(specificIndirect) < 1e-9 && projection.indirectEmissions < 1e-9
  const sectorKey = aggregatedGoodsCategory ?? ''
  const sectorSubjectToIndirectExclusionRule = sectorUsesIndirectEmissionExclusionRule(sectorKey)

  const benchmarkDisclaimer = result.parameters.benchmark.disclaimer?.message ?? null

  return {
    cnCode: good.cnCode,
    goodsName: resolveLiabilityOverviewGoodsName(good.cnCode, good.description, t),
    year: projectionYear,
    volumeTonnes: good.netMassInTonnes,
    quarter,
    sector: aggregatedGoodsCategory ?? null,
    specificDirectTco2PerT: projection.specificDirectEmbeddedEmissions ?? 0,
    specificIndirectTco2PerT: specificIndirect,
    cbamBenchmarkTco2PerT: benchmarkT,
    intermediateBenchmarkPerT,
    cbamFactor: projection.cbamFactor,
    cscfPercentage,
    discountPerTonneTco2,
    certificatePriceEurPerT,
    priceSource: quarterModifier?.isOfficialValueLocked ? 'official' : 'estimated',
    specificEmbeddedEmissionsSource: specificEmbedded.source,
    defaultMarkupPercent,
    totalDirectTco2: projection.directEmissions,
    totalIndirectTco2: projection.indirectEmissions,
    totalEmbeddedTco2: projection.totalEmissions,
    totalDiscountTco2: projection.freeAllocationTCo2,
    netEmissionsTco2: projection.netEmissions,
    liabilityEur: computeCbamLiabilityEuro(
      projection.netEmissions,
      certificatePriceEurPerT,
      6
    ),
    isUsingDefaultEmissions: good.isUsingDefaultEmissions,
    actualValueSource: supplierEmissionsReport?.evidenceFileName ?? null,
    defaultValueSource,
    routeLetter,
    indirectEffectivelyZero,
    sectorSubjectToIndirectExclusionRule,
    benchmarkDisclaimer
  }
}
